import { create } from "zustand";
import { toast } from "react-toastify";
import { API_BASE_URL1 } from "../../../../Services/baseApiUrl";
import { apiCall } from "../../../../../utils/apiCall";
import {
  AddRouteDetails,
  AssignVehicle,
  GetRouteAndCollectionPointMapDetailsById,
} from "../../../../Apis/routeMasterApi";
import {
  buildAddRouteDetailsPayload,
  parseRouteCoordinatesFromDetail,
  parseStoppagesFromDetail,
} from "../routeMasterFormUtils";
import { submitPayloadToDummyApi } from "../routeMasterDebugApi";

const formatRouteDate = (value) => {
  if (!value) return "-";

  const text = String(value).trim();
  const ddMmYyyyMatch = text.match(/^(\d{2})-(\d{2})-(\d{4})/);
  if (ddMmYyyyMatch) {
    return `${ddMmYyyyMatch[1]}-${ddMmYyyyMatch[2]}-${ddMmYyyyMatch[3]}`;
  }

  const date = new Date(text);
  if (Number.isNaN(date.getTime())) {
    return text.split(" ")[0] || "-";
  }

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const unwrapRouteListResponse = (payload) => {
  if (Array.isArray(payload)) {
    return { routes: payload, totalCount: payload.length };
  }

  if (Array.isArray(payload?.data)) {
    return {
      routes: payload.data,
      totalCount: payload.totalCount ?? payload.data.length,
    };
  }

  return { routes: [], totalCount: 0 };
};

const formatDistance = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const text = String(value).trim();
  return /km/i.test(text) ? text : `${text} Km`;
};

export const mapRouteItem = (item = {}) => {
  const vehicleDetails = item.vehicleDetails ?? item.VehicleDetails ?? null;
  const vehicleNumber =
    vehicleDetails?.vehicleNumber ??
    vehicleDetails?.otherNo ??
    vehicleDetails?.vehicleNo ??
    item.vehicleNo ??
    item.vehicleNumber ??
    item.vehicle ??
    "";

  return {
  routeId: item.routeId ?? item.id,
  routeCode: item.routeCode ?? item.route_code ?? "-",
  routeDescription: item.routeDescription ?? item.routeName ?? item.route_name ?? "-",
  routeName: item.routeName ?? item.routeDescription ?? item.route_name ?? "-",
  ward: item.wardName ?? item.circleName ?? item.ward ?? "-",
  wardName: item.wardName ?? item.circleName ?? item.ward ?? "-",
  vehicle: vehicleNumber || "-",
  vehicleId: item.vehicleId ?? vehicleDetails?.vehicleId ?? null,
  vehicleDetails,
  routeType: item.routeType ?? item.routeTypeName ?? "-",
  routeTypeName: item.routeTypeName ?? item.routeType ?? "-",
  stoppage: Array.isArray(item.stoppages)
    ? item.stoppages.length
    : item.stoppage ?? item.stoppageCount ?? item.stopCount ?? "-",
  distance: formatDistance(item.distance ?? item.routeDistance ?? item.totalDistanceKm),
  fromDate: formatRouteDate(
    item.fromDate ?? item.validFrom ?? item.from_date ?? item.createdDate
  ),
  toDate: formatRouteDate(
    item.toDate ?? item.validTo ?? item.to_date ?? item.applicableDurationToDate
  ),
  routeCoordinates: parseRouteCoordinatesFromDetail(item),
  startPointCoordinate: item.startPointCoordinate ?? item.startCoordinate ?? "",
  endPointCoordinate: item.endPointCoordinate ?? item.endCoordinate ?? "",
  totalDistanceKm: item.totalDistanceKm ?? "",
  startPoint: item.startPoint ?? item.startingPoint ?? "",
  endPoint: item.endPoint ?? item.endingPoint ?? "",
  stoppages: parseStoppagesFromDetail(item),
  applicableLocation: item.applicableLocation ?? item.location ?? "",
  applicableDurationToDate: item.applicableDurationToDate ?? item.toDate ?? "",
  routeApplicableFor: item.routeApplicableFor ?? "",
  applicableMts: item.applicableMts ?? "",
  applicableRecyclingCenter: item.applicableRecyclingCenter ?? "",
  colonyId: item.colonyId ?? item.applicableColonyId ?? "",
  zoneId: item.zoneId ?? item.applicableZoneId,
  circleId: item.circleId,
  wardId: item.wardId,
  routeSupervisorId: item.routeSupervisorId ?? item.routeSupervisorID ?? null,
  routeSupervisorName: item.routeSuperVisorName ?? item.routeSupervisorName ?? "-",
  zoneName: item.zoneName ?? "-",
  circleName: item.circleName ?? "-",
  status: item.status === true || item.status === "Active" ? "Active" : "Inactive",
  rawStatus: item.status,
};
};

const filterRoutes = (routes, searchTerm) => {
  const term = searchTerm.trim().toLowerCase();
  if (!term) return routes;

  return routes.filter((route) => {
    const routeCode = route.routeCode?.toString().toLowerCase() ?? "";
    const routeDescription = route.routeDescription?.toString().toLowerCase() ?? "";
    return routeCode.includes(term) || routeDescription.includes(term);
  });
};

const getErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data === "string" && data) return data;
  if (data?.message) return data.message;
  if (data?.errors && typeof data.errors === "object") {
    const messages = Object.values(data.errors).flat().filter(Boolean);
    if (messages.length) return messages.join(", ");
  }
  return fallback;
};

const updateRouteAssignmentInList = (routes, payload) => {
  const vehicleLabel =
    payload.assignedVehicles?.map((vehicle) => vehicle.vehicleNumber).join(", ") ||
    payload.newVehicle?.vehicleNumber ||
    "-";

  return routes.map((route) =>
    String(route.routeId) === String(payload.routeId)
      ? {
          ...route,
          vehicle: vehicleLabel,
          stoppages: payload.stoppages ?? route.stoppages,
          routeCoordinates: payload.routeCoordinates ?? route.routeCoordinates,
        }
      : route
  );
};

export const useRouteMasterStore = create((set, get) => ({
  routes: [],
  filteredRoutes: [],
  searchTerm: "",
  isLoading: false,
  isSaving: false,
  pagination: {
    currentPage: 1,
    pageSize: 5,
    totalCount: 0,
  },

  setSearchTerm: (searchTerm) => {
    const { routes, pagination } = get();
    const filteredRoutes = filterRoutes(routes, searchTerm);

    set({
      searchTerm,
      filteredRoutes,
      pagination: {
        ...pagination,
        currentPage: 1,
        totalCount: searchTerm.trim() ? filteredRoutes.length : routes.length,
      },
    });
  },

  fetchRoutes: async () => {
    set({ isLoading: true });

    try {
      const response = await apiCall({
        url: `${API_BASE_URL1}Routes/GetAllRouteDetails`,
        method: "get",
        token: localStorage.getItem("token"),
      });

      const { routes: routeList, totalCount } = unwrapRouteListResponse(response);
      const mappedRoutes = routeList.map(mapRouteItem);
      const { searchTerm } = get();
      const filteredRoutes = filterRoutes(mappedRoutes, searchTerm);

      set({
        routes: mappedRoutes,
        filteredRoutes,
        isLoading: false,
        pagination: {
          ...get().pagination,
          currentPage: 1,
          totalCount: searchTerm.trim() ? filteredRoutes.length : totalCount,
        },
      });

      return { success: true, data: filteredRoutes };
    } catch (error) {
      set({
        routes: [],
        filteredRoutes: [],
        isLoading: false,
        pagination: { ...get().pagination, currentPage: 1, totalCount: 0 },
      });
      toast.error(getErrorMessage(error, "Unable to load route list."));
      return { success: false };
    }
  },

  setPage: (currentPage) =>
    set((state) => ({
      pagination: { ...state.pagination, currentPage },
    })),

  setPageSize: (pageSize) =>
    set((state) => ({
      pagination: { ...state.pagination, pageSize, currentPage: 1 },
    })),

  fetchRouteDetailById: async (routeId) => {
    if (!routeId) return null;

    const cachedRoute = get().routes.find(
      (route) => String(route.routeId) === String(routeId)
    );

    try {
      // Always fetch map details — list rows usually don't include coordinates.
      const response = await GetRouteAndCollectionPointMapDetailsById(routeId);
      const raw = response?.data?.data ?? response?.data ?? response;
      const data = Array.isArray(raw) ? raw[0] : raw;
      const mapped = mapRouteItem(data || {});

      // Prefer fetched coordinates; fall back to any cached map points.
      if (
        (!mapped.routeCoordinates || mapped.routeCoordinates.length < 2) &&
        cachedRoute?.routeCoordinates?.length >= 2
      ) {
        mapped.routeCoordinates = cachedRoute.routeCoordinates;
      }

      const routes = get().routes.map((route) =>
        String(route.routeId) === String(routeId)
          ? { ...route, ...mapped }
          : route
      );
      set({
        routes,
        filteredRoutes: filterRoutes(routes, get().searchTerm),
      });

      return mapped;
    } catch {
      return cachedRoute ?? null;
    }
  },

  assignVehicleToRoute: async (payload) => {
    set({ isSaving: true });

    try {
      const requestBody = {
        routeId: Number(payload.routeId) || 0,
        assignedVehicleId: Number(payload.assignedVehicleId) || 0,
        reason: payload.reason?.trim() || "",
      };

      if (!requestBody.routeId) {
        throw new Error("RouteId is required to assign a vehicle.");
      }

      if (!requestBody.assignedVehicleId) {
        throw new Error("Please select a vehicle to assign.");
      }

      const response = await AssignVehicle(requestBody);
      const responseData = response?.data ?? response;
      const isSuccess =
        responseData?.success === true ||
        responseData?.success === "true" ||
        response?.status === 200;

      if (!isSuccess && responseData?.success === false) {
        throw new Error(
          responseData?.message || "Unable to assign vehicle."
        );
      }

      const vehicleLabel =
        payload.assignedVehicleNumber ||
        String(requestBody.assignedVehicleId);

      const routes = updateRouteAssignmentInList(get().routes, {
        routeId: requestBody.routeId,
        assignedVehicles: [
          {
            vehicleId: requestBody.assignedVehicleId,
            vehicleNumber: vehicleLabel,
          },
        ],
        stoppages: payload.stoppages,
        routeCoordinates: payload.routeCoordinates,
      });
      const filteredRoutes = filterRoutes(routes, get().searchTerm);

      set({
        routes,
        filteredRoutes,
        pagination: {
          ...get().pagination,
          totalCount: filteredRoutes.length,
        },
      });

      toast.success(responseData?.message || "Vehicle assigned successfully.");
      return { success: true, data: requestBody };
    } catch (error) {
      toast.error(getErrorMessage(error, "Unable to assign vehicle."));
      return { success: false };
    } finally {
      set({ isSaving: false });
    }
  },

  reassignVehicleToRoute: async (payload) => {
    set({ isSaving: true });

    try {
      const requestBody = {
        routeId: Number(payload.routeId) || 0,
        assignedVehicleId: Number(payload.assignedVehicleId) || 0,
        reason: payload.reason?.trim() || "",
      };

      if (!requestBody.routeId) {
        throw new Error("RouteId is required to reassign a vehicle.");
      }

      if (!requestBody.assignedVehicleId) {
        throw new Error("Please select a vehicle to reassign.");
      }

      const response = await AssignVehicle(requestBody);
      const responseData = response?.data ?? response;
      const isSuccess =
        responseData?.success === true ||
        responseData?.success === "true" ||
        response?.status === 200;

      if (!isSuccess && responseData?.success === false) {
        throw new Error(
          responseData?.message || "Unable to reassign vehicle."
        );
      }

      const vehicleLabel =
        payload.assignedVehicleNumber ||
        String(requestBody.assignedVehicleId);

      const routes = updateRouteAssignmentInList(get().routes, {
        routeId: requestBody.routeId,
        assignedVehicles: [
          {
            vehicleId: requestBody.assignedVehicleId,
            vehicleNumber: vehicleLabel,
          },
        ],
        stoppages: payload.stoppages,
        routeCoordinates: payload.routeCoordinates,
      });
      const filteredRoutes = filterRoutes(routes, get().searchTerm);

      set({
        routes,
        filteredRoutes,
        pagination: {
          ...get().pagination,
          totalCount: filteredRoutes.length,
        },
      });

      toast.success(responseData?.message || "Vehicle reassigned successfully.");
      return { success: true, data: requestBody };
    } catch (error) {
      toast.error(getErrorMessage(error, "Unable to reassign vehicle."));
      return { success: false };
    } finally {
      set({ isSaving: false });
    }
  },

  saveRoute: async (payload, isEdit = false) => {
    set({ isSaving: true });

    try {
      if (isEdit) {
        const requestBody = {
          ...payload,
          zoneId: Number(payload.zoneId),
          circleId: Number(payload.circleId),
          routeSupervisorId: payload.routeSupervisorId || null,
          status: payload.status === true || payload.status === "true",
        };

        await submitPayloadToDummyApi("Update Route", requestBody);

        toast.success(
          "Update Route payload sent to dummy API. Press F12 and check the console for full JSON."
        );

        return { success: true, payload: requestBody };
      }

      const normalizedPayload = {
        ...payload,
        zoneId: Number(payload.zoneId ?? payload.applicableZoneId) || 0,
        wardId: Number(payload.wardId ?? payload.applicableWardId) || 0,
      };

      const requestBody = buildAddRouteDetailsPayload(normalizedPayload);

      const response = await AddRouteDetails(requestBody);
      const responseData = response?.data ?? response;
      const isSuccess =
        responseData?.success === true ||
        responseData?.success === "true" ||
        response?.status === 200;

      if (!isSuccess && responseData?.success === false) {
        throw new Error(
          responseData?.message || "Unable to add route details."
        );
      }

      await get().fetchRoutes();

      toast.success(responseData?.message || "Route added successfully.");
      return { success: true, payload: requestBody, data: responseData };
    } catch (error) {
      toast.error(getErrorMessage(error, "Unable to submit route payload."));
      return { success: false };
    } finally {
      set({ isSaving: false });
    }
  },
}));
